import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  PlusIcon,
  TrashIcon,
  UserIcon,
  PhoneIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline';
import { leadService } from '../../services/leadService';
import { campaignService } from '../../services/campaignService';
import { Lead, LeadStatus } from '../../types/lead';
import { Campaign } from '../../types/campaign';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Breadcrumb from '../../components/UI/Breadcrumb';
import Button from '../../components/UI/Button';
import ErrorMessage from '../../components/UI/ErrorMessage';
import StatusBadge from '../../components/UI/StatusBadge';
import Modal from '../../components/UI/Modal';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import { useAuth } from '../../contexts/AuthContext';

const editLeadSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  phoneE164: z.string()
    .min(1, 'Phone number is required')
    .regex(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number in E.164 format (e.g., +15551234567)'),
  extraPhones: z.array(z.object({
    value: z.string().regex(/^\+[1-9]\d{1,14}$/, 'Invalid E.164 phone number').or(z.literal('')),
  })).optional(),
  email: z.string().email('Please enter a valid email address').optional().or(z.literal('')),
  companyName: z.string().optional().or(z.literal('')),
  status: z.enum([
    'new',
    'called',
    'queued',
    'calling',
    'completed',
    'failed',
    'interested',
    'not_interested',
    'do_not_call',
    'invalid',
    'no_answer',
    'app_out',
    'unreachable',
    'follow_up',
    'out_of_business',
    'wrong_number',
    'unqualified',
    'busy',
    'app_in',
    'gatekeeper',
    'operator',
    'deceased',
    'spanish',
    'submitted',
    'contract',
    'funded',
  ] as const),
  customFields: z.array(z.object({
    key: z.string().min(1, 'Field name is required'),
    value: z.string().min(1, 'Field value is required')
  })).optional()
});

type EditLeadFormData = z.infer<typeof editLeadSchema>;

const EditLeadPage: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState<Lead | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDoNotCallConfirm, setShowDoNotCallConfirm] = useState(false);
  // Treat organization/member role as restricted for editing
  const isRestricted = user?.role === 'member';

  const { register, handleSubmit, control, formState: { errors }, reset, setValue } = useForm<EditLeadFormData>({
    resolver: zodResolver(editLeadSchema)
  });

  const { fields: extraPhoneFields, append: appendPhone, remove: removePhone } = useFieldArray({
    control,
    name: 'extraPhones'
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customFields'
  });

  const fetchCampaign = async (campaignId: string) => {
    try {
      const campaignData = await campaignService.getCampaign(campaignId);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    }
  };

  useEffect(() => {
    const fetchLead = async () => {
      if (!leadId) return;
      
      try {
        setLoading(true);
        const leadData = await leadService.getLead(leadId);
        setLead(leadData);
        
        // Fetch campaign data
        if (leadData.campaignId) {
          await fetchCampaign(leadData.campaignId);
        }
        
        // Build extra phone numbers from API response
        const extraPhonesFromApi = (leadData.phoneNumbers || [])
          .filter((pn: any) => pn.phoneNumber !== leadData.phoneE164)
          .map((pn: any) => ({ value: pn.phoneNumber }));

        // Reset form with lead data
        reset({
          fullName: leadData.fullName,
          phoneE164: leadData.phoneE164,
          extraPhones: extraPhonesFromApi,
          email: leadData.email || '',
          companyName: leadData.companyName || '',
          status: leadData.status,
          customFields: Object.entries(leadData.custom || {}).map(([key, value]) => ({
            key,
            value: String(value)
          }))
        });
      } catch (error) {
        console.error('Error fetching lead:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [leadId, reset]);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters except +
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // Ensure it starts with +
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    
    return cleaned;
  };



  const onSubmit = async (data: EditLeadFormData) => {
    if (!leadId) return;
    
    try {
      setSubmitting(true);
      
      // Convert custom fields array back to object
      const customFields = data.customFields?.reduce((acc, field) => {
        acc[field.key] = field.value;
        return acc;
      }, {} as Record<string, any>) || {};

      const extraNums = (data.extraPhones || [])
        .map((p) => p.value)
        .filter((v) => v && /^\+[1-9]\d{1,14}$/.test(v));
      const allPhones = [data.phoneE164, ...extraNums];

      await leadService.updateLead(leadId, {
        fullName: data.fullName,
        phoneE164: data.phoneE164,
        email: data.email || undefined,
        companyName: data.companyName || undefined,
        status: data.status,
        custom: customFields,
        phoneNumbers: allPhones.length > 0 ? allPhones : undefined,
      });

      if (lead) {
        navigate(`/campaigns/${lead.campaignId}/leads`);
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoNotCall = async () => {
    if (!leadId) return;
    
    try {
      setSubmitting(true);
      await leadService.markDoNotCall(leadId);
      setShowDoNotCallConfirm(false);
      
      // Update local state
      if (lead) {
        setLead({ ...lead, status: 'do_not_call' });
        setValue('status', 'do_not_call');
      }
    } catch (error) {
      console.error('Error marking lead as do not call:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-semibold text-foreground">Lead not found</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          The lead you're looking for doesn't exist or you don't have access to it.
        </p>
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <h1 className="text-2xl font-bold text-foreground">Insufficient Permissions</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Your account is restricted. You can view campaigns and results, but cannot create or edit campaigns or leads. Please contact your administrator if you need elevated access.
            </p>
          </CardContent>
        </Card>
        <div className="flex justify-end">
          <Button type="button" onClick={() => lead && navigate(`/campaigns/${lead.campaignId}/leads`)} variant="primary">
            Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign?.name || 'Campaign', href: `/campaigns/${lead.campaignId}` },
          { label: 'Leads', href: `/campaigns/${lead.campaignId}/leads` },
          { label: lead.fullName, href: `/campaigns/${lead.campaignId}/leads/${leadId}` },
          { label: 'Edit', current: true }
        ]}
      />
      
      {/* Header */}
      <Card>
        <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Edit Lead</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Update lead information and status.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <StatusBadge status={lead.status} type="lead" />
            {lead.status !== 'do_not_call' && (
              <Button
                onClick={() => setShowDoNotCallConfirm(true)}
                variant="destructive"
                size="sm"
              >
                <NoSymbolIcon className="-ml-0.5 mr-2 h-4 w-4" />
                Mark Do Not Call
              </Button>
            )}
          </div>
        </div>
        </CardContent>
      </Card>

      {/* Lead Info */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="flex items-center">
              <UserIcon className="h-5 w-5 text-muted-foreground mr-2" />
              <div>
                <div className="text-sm font-medium text-foreground">{lead.fullName}</div>
                <div className="text-sm text-muted-foreground">Lead Name</div>
              </div>
            </div>
            <div className="flex items-center">
              <PhoneIcon className="h-5 w-5 text-muted-foreground mr-2" />
              <div>
                <div className="text-sm font-medium text-foreground">{lead.phoneE164}</div>
                <div className="text-sm text-muted-foreground">Phone Number</div>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-foreground">
                {lead.lastCallAt ? new Date(lead.lastCallAt).toLocaleDateString() : 'Never'}
              </div>
              <div className="text-sm text-muted-foreground">Last Contact</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-5 sm:p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-foreground">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    {...register('fullName')}
                    className="input"
                    placeholder="Enter full name"
                  />
                  <ErrorMessage message={errors.fullName?.message} />
                </div>

                <div>
                  <label htmlFor="phoneE164" className="block text-sm font-medium text-foreground">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phoneE164"
                    {...register('phoneE164', {
                      onChange: (e) => {
                        e.target.value = formatPhoneNumber(e.target.value);
                      }
                    })}
                    className="input"
                    placeholder="+15551234567"
                  />
                  <ErrorMessage message={errors.phoneE164?.message} />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Enter in E.164 format (e.g., +15551234567)
                  </p>
                </div>

                {/* Extra phone numbers */}
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-foreground">
                      Additional Phone Numbers
                    </label>
                    <Button
                      type="button"
                      onClick={() => appendPhone({ value: '' })}
                      variant="outlined"
                      size="sm"
                    >
                      <PlusIcon className="-ml-0.5 mr-1 h-4 w-4" />
                      Add Number
                    </Button>
                  </div>
                  {extraPhoneFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2 mb-2">
                      <input
                        type="tel"
                        {...register(`extraPhones.${index}.value`, {
                          onChange: (e) => {
                            e.target.value = formatPhoneNumber(e.target.value);
                          }
                        })}
                        className="input flex-1"
                        placeholder="+15559876543"
                      />
                      <button
                        type="button"
                        onClick={() => removePhone(index)}
                        className="text-destructive hover:text-destructive/80 p-1"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {lead?.phoneNumbers && lead.phoneNumbers.filter((pn) => !pn.isActive).length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      <span className="font-medium">Archived numbers:</span>{' '}
                      {lead.phoneNumbers
                        .filter((pn) => !pn.isActive)
                        .map((pn) => `${pn.phoneNumber} (${pn.status})`)
                        .join(', ')}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register('email')}
                    className="input"
                    placeholder="Enter email address"
                  />
                  <ErrorMessage message={errors.email?.message} />
                </div>

                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-foreground">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    {...register('companyName')}
                    className="input"
                    placeholder="Enter company name"
                  />
                  <ErrorMessage message={errors.companyName?.message} />
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-foreground">
                    Status
                  </label>
                  <select
                    id="status"
                    {...register('status')}
                    className="input"
                  >
                    <option value="new">New</option>
                    <option value="queued">Queued</option>
                    <option value="calling">Calling</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="interested">Interested</option>
                    <option value="not_interested">Not Interested</option>
                    <option value="do_not_call">Do Not Call</option>
                    <option value="invalid">Invalid</option>
                    <option value="no_answer">No Answer</option>
                    <option value="unreachable">Unreachable</option>
                    <option value="follow_up">Follow Up</option>
                    <option value="out_of_business">Out of Business</option>
                    <option value="wrong_number">Wrong Number</option>
                    <option value="unqualified">Unqualified</option>
                    <option value="app_in">App In</option>
                    <option value="app_out">App Out</option>
                    <option value="deceased">Deceased</option>
                    <option value="spanish">Spanish</option>
                  </select>
                  <ErrorMessage message={errors.status?.message} />
                </div>
              </div>
            </div>

            {/* Custom Fields */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Custom Fields
                </h3>
                <Button
                  type="button"
                  onClick={() => append({ key: '', value: '' })}
                  variant="outlined"
                  size="sm"
                >
                  <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                  Add Field
                </Button>
              </div>
              
              {fields.length > 0 && (
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-end space-x-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-foreground">
                          Field Name
                        </label>
                        <input
                          type="text"
                          {...register(`customFields.${index}.key`)}
                          className="input"
                          placeholder="Enter field name"
                        />
                        <ErrorMessage message={errors.customFields?.[index]?.key?.message} />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-foreground">
                          Field Value
                        </label>
                        <input
                          type="text"
                          {...register(`customFields.${index}.value`)}
                          className="input"
                          placeholder="Enter field value"
                        />
                        <ErrorMessage message={errors.customFields?.[index]?.value?.message} />
                      </div>
                      <Button
                        type="button"
                        onClick={() => remove(index)}
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {fields.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  No custom fields added. Click "Add Field" to add custom information.
                </p>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-border">
              <Button
                type="button"
                onClick={() => lead && navigate(`/campaigns/${lead.campaignId}/leads`)}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                variant="primary"
                loading={submitting}
              >
                {submitting ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Updating...
                  </>
                ) : (
                  'Update Lead'
                )}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {/* Do Not Call Confirmation Dialog */}
      <Modal
        isOpen={showDoNotCallConfirm}
        onClose={() => setShowDoNotCallConfirm(false)}
        title="Mark as Do Not Call"
        size="md"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-destructive/10 mb-4">
              <NoSymbolIcon className="h-6 w-6 text-destructive" />
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Are you sure you want to mark this lead as "Do Not Call"? This action will prevent any future calls to this lead.
          </p>
          <div className="flex justify-center space-x-3">
            <Button
              onClick={() => setShowDoNotCallConfirm(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDoNotCall}
              disabled={submitting}
              variant="destructive"
              loading={submitting}
            >
              {submitting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Processing...
                </>
              ) : (
                'Mark Do Not Call'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditLeadPage;